/**
 * Poseidon dashboard — every confirmable control of §3.1, in one file.
 *
 * Keeping all of them together is deliberate: §3.1 lists exactly which actions
 * require explicit confirmation, and the cheapest way to guarantee none is
 * forgotten (or silently given a weaker confirmation) is to make this file the
 * single place they are defined.
 *
 * | §3.1 control                        | component                  |
 * |-------------------------------------|----------------------------|
 * | enable/disable demo execution        | ExecutionModeControl       |
 * | approve/reject an order intent       | ApproveRejectIntentControls|
 * | cancel an active order               | CancelOrderControl         |
 * | close a position                     | ClosePositionControl       |
 * | activate soft halt / hard halt       | HaltControls               |
 * | modify risk limits                   | RiskLimitEditControl       |
 * | modify allow lists                   | AllowListToggleControl     |
 */
import { useState } from 'react';
import type {
  AllowListEntryView,
  ExecutionMode,
  HaltStateView,
  PositionRow,
  ReadinessView,
  RiskLimitRow,
  SignalRow,
  WorkingOrderRow,
} from '../api/types';
import { CONFIRMATION_PHRASES } from '../config';
import { formatNumber } from '../lib/format';
import { useCommand } from '../hooks/useCommand';
import { ConfirmDialog, ConfirmableAction } from './ConfirmDialog';
import { Badge } from './primitives';

/* ------------------------------------------------------------------ *
 * Halt controls (§7.3)
 * ------------------------------------------------------------------ */

export function HaltControls({ halt, mock = false }: { halt: HaltStateView; mock?: boolean }): JSX.Element {
  const halted = halt.level !== 'none';
  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="data-clock">
        <span className="text-faint">Current state</span>
        <Badge tone={halt.level === 'hard' ? 'danger' : halt.level === 'soft' ? 'warn' : 'ok'}>
          {halt.level === 'hard' ? 'HARD HALT' : halt.level === 'soft' ? 'SOFT HALT' : 'no halt'}
        </Badge>
        <span className="text-faint">
          new entries {halt.allows_new_entries ? 'permitted' : 'blocked'} · protective actions{' '}
          {halt.allows_protective_actions ? 'permitted' : 'blocked'}
        </span>
        {mock && <span className="mock-note">mock state</span>}
      </div>

      <div className="field-row">
        <ConfirmableAction
          label="Soft halt"
          buttonClassName="btn btn--sm btn--warn"
          severity="soft-halt"
          title="Activate soft halt"
          command="set_halt"
          payload={{ level: 'soft', reason: 'Operator soft halt from the dashboard.' }}
          confirmationPhrase={CONFIRMATION_PHRASES.soft_halt}
          requireReason
          reasonLabel="Reason for the soft halt (recorded in the audit log)"
          reasonPlaceholder="e.g. Spreads widened beyond the strategy assumption during the London open."
          disabled={halt.level === 'soft'}
          disabledReason={halt.level === 'soft' ? 'A soft halt is already active.' : null}
          confirmLabel="Activate soft halt"
          description={
            <>
              A soft halt blocks <strong>new entries</strong> while still allowing protective changes, exits and cancels. Use it when
              conditions are wrong but open risk still needs managing.
            </>
          }
          consequences={[
            'New order intents are rejected by the risk engine until the halt is released.',
            'Stop changes, position closes and order cancellations keep working.',
            'A halt event is written to the audit ledger with your operator id and reason.',
          ]}
        />

        <ConfirmableAction
          label="Hard halt"
          buttonClassName="btn btn--sm btn--danger"
          severity="hard-halt"
          title="Activate hard halt"
          command="set_halt"
          payload={{ level: 'hard', reason: 'Operator hard halt from the dashboard.' }}
          confirmationPhrase={CONFIRMATION_PHRASES.hard_halt}
          requireReason
          reasonLabel="Reason for the hard halt (recorded in the audit log)"
          reasonPlaceholder="e.g. MT5/ledger mismatch detected; stopping all submission until reconciled."
          disabled={halt.level === 'hard'}
          disabledReason={halt.level === 'hard' ? 'A hard halt is already active.' : null}
          confirmLabel="Activate HARD halt"
          description={
            <>
              A hard halt blocks <strong>all submissions</strong> except the explicit emergency-close workflow. This is the strongest
              stop available from the dashboard.
            </>
          }
          consequences={[
            'Every order submission path is blocked, including approved intents.',
            'Only the explicit emergency-close workflow remains available.',
            'Protective stop modifications are blocked as well; existing broker-side stops keep resting at the broker.',
            'A halt event is written to the audit ledger with your operator id and reason.',
          ]}
        />

        <ConfirmableAction
          label="Release halt"
          buttonClassName="btn btn--sm"
          severity="normal"
          title="Release halt and return to normal mode"
          command="set_halt"
          payload={{ level: 'none', reason: 'Operator released the halt after review.' }}
          confirmationPhrase={CONFIRMATION_PHRASES.release_halt}
          requireReason
          reasonLabel="Reason for releasing the halt (recorded in the audit log)"
          reasonPlaceholder="e.g. Reconciled positions against the EA snapshot; spreads back inside the cap."
          disabled={!halted}
          disabledReason={!halted ? 'No halt is active.' : null}
          confirmLabel="Release halt"
          description={<>Releasing the halt returns the system to normal mode. Confirm that the condition which caused the halt is actually resolved.</>}
          consequences={[
            'New entries become possible again, subject to every other risk rule.',
            'The release is recorded against your operator id with this reason.',
          ]}
        />
      </div>

      <p className="text-faint" style={{ margin: 0, fontSize: 11.5 }}>
        §7.3 also requires a configuration-based emergency endpoint protected by a separate local secret for the case where this UI is
        unavailable. That endpoint is not part of the dashboard and cannot be triggered from here.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Execution mode (§19.4)
 * ------------------------------------------------------------------ */

const MODE_DESCRIPTION: Record<ExecutionMode, string> = {
  disabled: 'No order can be submitted by any path.',
  approval_required: 'A risk-approved intent waits for an operator click before submission.',
  demo_enabled: 'A risk-approved intent is forwarded to the EA without a human click (constrained_auto).',
};

export function ExecutionModeControl({
  mode,
  readiness,
}: {
  mode: ExecutionMode;
  readiness: ReadinessView | null;
}): JSX.Element {
  const blocked = readiness === null || !readiness.demo_mode.pass || !readiness.bridge_auth.pass || !readiness.database.pass;

  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="data-clock">
        <span className="text-faint">Execution</span>
        <Badge tone={mode === 'demo_enabled' ? 'warn' : mode === 'approval_required' ? 'info' : 'muted'}>{mode}</Badge>
        <span className="text-faint">{MODE_DESCRIPTION[mode]}</span>
      </div>

      {readiness !== null && (
        <div className={blocked ? 'halt-strip halt-strip--soft' : 'disclosure'}>
          {blocked ? (
            <>
              <strong>Pre-flight blockers present.</strong>
              <span>
                Demo mode, bridge authentication or the database check is failing. §7.1 requires these to fail closed, so execution
                must not be enabled.
              </span>
            </>
          ) : (
            <>
              Pre-flight readiness: <strong>{readiness.overall}</strong> · {readiness.checks.filter((check) => check.status === 'pass').length} of{' '}
              {readiness.checks.length} checks passing. Enabling execution is still a human decision, recorded with a reason.
            </>
          )}
        </div>
      )}

      <div className="field-row">
        <ConfirmableAction
          label="Set approval-required"
          buttonClassName="btn btn--sm"
          title="Require approval for every order"
          command="set_execution_mode"
          payload={{ mode: 'approval_required' satisfies ExecutionMode }}
          confirmationPhrase={CONFIRMATION_PHRASES.set_execution_mode}
          requireReason
          reasonLabel="Reason for the execution-mode change"
          reasonPlaceholder="e.g. Returning to per-order approval after the walk-forward review."
          disabled={mode === 'approval_required' || blocked}
          disabledReason={mode === 'approval_required' ? 'Already in approval-required mode.' : blocked ? 'Blocked by a failing pre-flight check.' : null}
          confirmLabel="Require approval"
          description={<>Every risk-approved intent will wait for an explicit operator approval before the EA receives it.</>}
          consequences={[
            'Order intents accumulate in the approval queue until you act on them.',
            'Expired intents are rejected, not silently executed.',
            'The change is recorded in the audit ledger.',
          ]}
        />

        <ConfirmableAction
          label="Enable demo execution"
          buttonClassName="btn btn--sm btn--primary"
          severity="destructive"
          title="Enable constrained automated demo execution"
          command="set_execution_mode"
          payload={{ mode: 'demo_enabled' satisfies ExecutionMode }}
          confirmationPhrase={CONFIRMATION_PHRASES.set_execution_mode}
          requireReason
          reasonLabel="Reason for enabling automated execution"
          reasonPlaceholder="e.g. Phase 3 gate cleared: 20+ demo lifecycles with no unexplained duplicates."
          disabled={mode === 'demo_enabled' || blocked}
          disabledReason={mode === 'demo_enabled' ? 'Demo execution is already enabled.' : blocked ? 'Blocked by a failing pre-flight check.' : null}
          confirmLabel="Enable demo execution"
          description={
            <>
              Under <span className="text-mono">constrained_auto</span> a risk-engine <strong>APPROVE</strong> is forwarded to the EA
              without a human click. The risk engine keeps full authority; the model still cannot originate or override a trade.
            </>
          }
          consequences={[
            'Orders can be submitted to the MT5 DEMO account without a per-order click.',
            'Kill switches, soft/hard halt and the emergency-close workflow stay available.',
            'Every decision still writes the full trail (signal, regime, research, risk, order/deal result) for after-the-fact review.',
            '§19.4 requires a further fixed number of clean demo lifecycles before this mode is used; confirm that gate is met.',
          ]}
        />

        <ConfirmableAction
          label="Disable execution"
          buttonClassName="btn btn--sm"
          title="Disable all order submission"
          command="set_execution_mode"
          payload={{ mode: 'disabled' satisfies ExecutionMode }}
          confirmationPhrase={CONFIRMATION_PHRASES.set_execution_mode}
          requireReason
          reasonLabel="Reason for disabling execution"
          reasonPlaceholder="e.g. Ending the demo session; no further submissions today."
          disabled={mode === 'disabled'}
          disabledReason={mode === 'disabled' ? 'Execution is already disabled.' : null}
          confirmLabel="Disable execution"
          description={<>No path will be able to submit an order. This is the safe resting state.</>}
          consequences={[
            'All new submissions are refused by the daemon.',
            'Open positions keep their broker-side stops; closing them requires re-enabling or the emergency workflow.',
            'The change is recorded in the audit ledger.',
          ]}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Order intent approval / rejection (§9.2 step 6)
 * ------------------------------------------------------------------ */

export function ApproveRejectIntentControls({ signal }: { signal: SignalRow }): JSX.Element {
  const awaiting = signal.status === 'awaiting_approval';
  const description = (
    <>
      <div>
        {signal.symbol} {signal.side} · strategy <span className="text-mono">{signal.strategy_version}</span>
      </div>
      <div className="kv" style={{ marginTop: 8 }}>
        <span className="kv__key">Decision</span>
        <span className="kv__value text-mono">{signal.decision_id}</span>
        <span className="kv__key">Proposed entry</span>
        <span className="kv__value">{formatNumber(signal.proposed_entry, 5)}</span>
        <span className="kv__key">Stop / target</span>
        <span className="kv__value">
          {formatNumber(signal.stop_loss, 5)} / {formatNumber(signal.take_profit, 5)}
        </span>
        <span className="kv__key">Quant score → final</span>
        <span className="kv__value">
          {formatNumber(signal.quant_score, 3)} → {signal.final_score === null ? '—' : formatNumber(signal.final_score, 3)}
        </span>
      </div>
    </>
  );

  return (
    <div className="field-row">
      <ConfirmableAction
        label="Approve"
        buttonClassName="btn btn--sm btn--ok"
        title="Approve this order intent"
        command="approve_order_intent"
        payload={{ decision_id: signal.decision_id, order_intent_id: null, reason: null }}
        confirmationPhrase={CONFIRMATION_PHRASES.approve_order_intent}
        disabled={!awaiting}
        disabledReason={awaiting ? null : `Not awaiting approval (status: ${signal.status}).`}
        confirmLabel="Approve order"
        description={description}
        consequences={[
          'The intent becomes eligible for submission to the MT5 demo account.',
          'It is still subject to the EA order_check and every downstream risk rule.',
          'Approval is recorded against your operator id and expires if not filled.',
        ]}
      />
      <ConfirmableAction
        label="Reject"
        buttonClassName="btn btn--sm btn--danger"
        severity="destructive"
        title="Reject this order intent"
        command="reject_order_intent"
        payload={{ decision_id: signal.decision_id, order_intent_id: null, reason: null }}
        confirmationPhrase={CONFIRMATION_PHRASES.reject_order_intent}
        requireReason
        reasonLabel="Reason for rejection (recorded in the audit log)"
        reasonPlaceholder="e.g. Event risk inside the holding window; not taking this one."
        disabled={!awaiting}
        disabledReason={awaiting ? null : `Not awaiting approval (status: ${signal.status}).`}
        confirmLabel="Reject order"
        description={description}
        consequences={[
          'The intent is dropped and will never be submitted.',
          'No order is sent to MT5; nothing is created at the broker.',
          'The rejection is recorded against your operator id with this reason.',
        ]}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cancel a working order
 * ------------------------------------------------------------------ */

export function CancelOrderControl({ order }: { order: WorkingOrderRow }): JSX.Element {
  return (
    <ConfirmableAction
      label={order.cancellable ? 'Cancel' : 'Cancel unavailable'}
      buttonClassName="btn btn--sm"
      title={`Cancel working order ${order.order_ticket}`}
      command="cancel_order"
      payload={{ order_ticket: order.order_ticket, symbol: order.symbol }}
      confirmationPhrase={CONFIRMATION_PHRASES.cancel_order}
      disabled={!order.cancellable}
      disabledReason={order.cancel_blocked_reason}
      confirmLabel="Cancel order"
      description={
        <>
          {order.symbol} {order.side} {formatNumber(order.lots, 2)} lots at {formatNumber(order.requested_price, 5)} · ticket{' '}
          <span className="text-mono">{order.order_ticket}</span>, state {order.order_state}
        </>
      }
      consequences={[
        'A cancellation request is sent to the EA and acknowledged by the MT5 terminal.',
        'A race with an in-flight fill is possible; the result is reconciled against broker deals.',
        'The cancellation is recorded in the audit ledger.',
      ]}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Close a position
 * ------------------------------------------------------------------ */

export function ClosePositionControl({ position, haltLevel }: { position: PositionRow; haltLevel: HaltStateView['level'] }): JSX.Element {
  const hardHalted = haltLevel === 'hard';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <ConfirmableAction
        label={hardHalted ? 'Emergency close' : 'Close position'}
        buttonClassName={hardHalted ? 'btn btn--sm btn--danger' : 'btn btn--sm'}
        severity={hardHalted ? 'destructive' : 'normal'}
        title={`Close ${position.symbol} position ${position.position_ticket}`}
        command="close_position"
        payload={{ position_ticket: position.position_ticket, symbol: position.symbol, lots: null, emergency: hardHalted }}
        confirmationPhrase={CONFIRMATION_PHRASES.close_position}
        confirmLabel="Close position"
        description={
          <>
            {position.symbol} {position.side} {formatNumber(position.lots, 2)} lots from {formatNumber(position.entry_price, 5)} · unrealized{' '}
            {formatNumber(position.unrealized_pnl, 2)} · ticket <span className="text-mono">{position.position_ticket}</span>
            {hardHalted && (
              <div className="text-warn" style={{ marginTop: 6 }}>
                A hard halt is active, so this goes through the explicit emergency-close workflow. That is the only submission path a
                hard halt permits.
              </div>
            )}
          </>
        }
        consequences={[
          'The full position volume is closed at market; slippage against the current quote is possible.',
          'The close is sent to the EA, and the resulting broker deals are reconciled against the internal ledger.',
          'The action is recorded in the audit ledger against your operator id.',
        ]}
      />
      {!position.stop_confirmed_at_broker && (
        <span className="text-warn" style={{ fontSize: 11 }}>
          Stop not confirmed resting at the broker — this position is not protected if the application stops.
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Risk limit edit (§3.2 D, with the second confirmation and reason)
 * ------------------------------------------------------------------ */

export function RiskLimitEditControl({ limit, mock = false }: { limit: RiskLimitRow; mock?: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState(String(limit.configured_value));
  const commandState = useCommand(() => setOpen(false));

  const isNumeric = typeof limit.configured_value === 'number';
  const parsed = isNumeric ? Number(newValue) : newValue;
  const invalid = isNumeric && (newValue.trim().length === 0 || Number.isNaN(parsed));
  const exceedsCeiling =
    isNumeric && limit.hard_ceiling !== null && typeof parsed === 'number' && parsed > limit.hard_ceiling;

  const disabled = !limit.editable;

  return (
    <>
      <button
        type="button"
        className="btn btn--sm"
        disabled={disabled}
        title={disabled ? 'This limit is not editable from the dashboard.' : `Change ${limit.label}`}
        onClick={() => {
          commandState.reset();
          setNewValue(String(limit.configured_value));
          setOpen(true);
        }}
      >
        Edit
      </button>

      <ConfirmDialog
        open={open}
        title={`Change ${limit.label}`}
        severity="destructive"
        confirmationPhrase={CONFIRMATION_PHRASES.set_risk_limit}
        requireReason
        reasonLabel="Reason for the change (stored with the audit record)"
        reasonPlaceholder="e.g. Tightening the daily cap for the first week of forward testing."
        confirmLabel="Apply change"
        busy={commandState.busy}
        error={commandState.error}
        onCancel={() => setOpen(false)}
        onConfirm={({ confirmation, reason }) => {
          void commandState.run(
            'set_risk_limit',
            {
              limit_id: limit.limit_id,
              new_value: parsed,
              reason: reason ?? '',
              replay_session_date: null,
            },
            { confirmation, reason },
          );
        }}
        description={
          <div className="grid" style={{ gap: 10 }}>
            <div className="kv">
              <span className="kv__key">Current value</span>
              <span className="kv__value">{limit.configured_display}</span>
              <span className="kv__key">Utilization</span>
              <span className="kv__value">{limit.utilization_display}</span>
              <span className="kv__key">On breach</span>
              <span className="kv__value">{limit.breach_action}</span>
              <span className="kv__key">Config version</span>
              <span className="kv__value text-mono">{limit.config_version}</span>
              {limit.hard_ceiling !== null && (
                <>
                  <span className="kv__key">Hard ceiling</span>
                  <span className="kv__value">
                    {limit.hard_ceiling} (config may not exceed this regardless of what is entered)
                  </span>
                </>
              )}
            </div>

            <label>
              New value
              <input
                type="text"
                value={newValue}
                spellCheck={false}
                onChange={(event) => setNewValue(event.target.value)}
                aria-invalid={invalid || exceedsCeiling}
              />
              {invalid && <span className="text-warn">A numeric value is required for this limit.</span>}
              {exceedsCeiling && <span className="text-danger">This exceeds the configured hard ceiling of {limit.hard_ceiling}.</span>}
            </label>

            {mock && <span className="mock-note">mock change</span>}
          </div>
        }
        consequences={[
          `The prior value ("${limit.configured_display}") and the new value are both written to the risk audit log.`,
          'The operator identity, timestamp and your reason are recorded alongside them.',
          'A new risk config version is created; historical decisions keep the config hash that produced them.',
          '§16.3.3 expects a risk-config change to be replayed against a historical session before it takes effect. This skeleton records the request but cannot run that replay.',
        ]}
      />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Allow-list toggle (§3.1)
 * ------------------------------------------------------------------ */

export function AllowListToggleControl({ entry }: { entry: AllowListEntryView }): JSX.Element {
  const nextEnabled = !entry.enabled;
  return (
    <ConfirmableAction
      label={nextEnabled ? 'Enable' : 'Disable'}
      buttonClassName="btn btn--sm"
      severity={nextEnabled ? 'normal' : 'destructive'}
      title={`${nextEnabled ? 'Enable' : 'Disable'} ${entry.value} in the ${entry.list_kind.replace('_', ' ')} allow list`}
      command="set_allow_list"
      payload={{ list_kind: entry.list_kind, entry_id: entry.entry_id, value: entry.value, enabled: nextEnabled }}
      confirmationPhrase={CONFIRMATION_PHRASES.set_allow_list}
      requireReason
      reasonLabel="Reason for the allow-list change"
      reasonPlaceholder="e.g. Swap costs during rollover make the demo results unrepresentative."
      confirmLabel={nextEnabled ? 'Enable entry' : 'Disable entry'}
      description={
        <>
          {entry.value} will be <strong>{nextEnabled ? 'enabled' : 'disabled'}</strong> for {entry.list_kind.replace('_', ' ')} eligibility.
        </>
      }
      consequences={[
        nextEnabled
          ? 'The instrument/session becomes eligible again and strategies may signal on it.'
          : 'No new position may be opened for this entry; existing positions keep their protective stops.',
        'The change is versioned and recorded with your operator id and reason.',
      ]}
    />
  );
}
